import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Why is SMS blocked?" panel for the shop-admin Dashboard.
 *
 * Sits next to <SmsUsageCard/>. Reads GET /api/sms/status and surfaces
 * the FIRST failing gate so the operator knows exactly what to fix. If
 * every gate is green, renders a small "SMS ready" chip instead.
 *
 * Reason strings map:
 *   sms_disabled            → shop admin's own on/off toggle is OFF
 *   subscription_inactive   → no active/trialing Stripe subscription
 *   trial_cap_reached       → used all 10 trial SMS credits
 *   monthly_cap_reached     → hit the plan cap (+ any bundle credits)
 *   nickname_missing        → sms_display_name not set on Shop profile
 */
const REASON_COPY = {
  sms_disabled: {
    title: 'SMS is switched off',
    body: 'Turn it on in Settings → Messaging to start sending reminders.',
    cta: { label: 'Open messaging settings', to: '/settings/messaging' },
  },
  subscription_inactive: {
    title: 'No active subscription',
    body: 'Subscribe to a plan to unlock SMS reminders and confirmations.',
    cta: { label: 'Choose a plan', to: '/billing' },
  },
  trial_cap_reached: {
    title: 'Trial SMS credits used up',
    body: 'You\'ve used all 10 free trial credits. Subscribe to unlock your full monthly cap.',
    cta: { label: 'Subscribe now', to: '/billing' },
  },
  monthly_cap_reached: {
    title: 'Monthly SMS cap reached',
    body: 'You\'ve sent every included message for this month. Top up an SMS bundle to keep sending.',
    cta: { label: 'Top up SMS', to: '/billing' },
  },
  nickname_missing: {
    title: 'SMS sender name not set',
    body: 'Add a short SMS nickname on your Shop profile so customers know who the messages are from.',
    cta: { label: 'Set SMS name', to: '/profile' },
  },
};

export const SmsStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(`${BACKEND_URL}/api/sms/status`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const j = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
        setStatus(j);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) return null;                 // fail-silent — this is an aux widget
  if (!status) return null;                // still loading — keep dashboard quiet

  // Everything green → small confirmation chip. The main "ready to send"
  // signal is already carried by the usage bar next to us, so this stays
  // deliberately understated.
  if (status.can_send) {
    return (
      <div
        className="panel"
        data-testid="sms-status-ready"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          color: 'var(--mute)',
        }}
      >
        <CheckCircle2 size={16} color="var(--green, #22c55e)" />
        <span>SMS is ready to send{status.subscription_status === 'trialing' && status.trial_remaining != null
          ? ` — ${status.trial_remaining} trial credits left`
          : ''}.</span>
      </div>
    );
  }

  // Blocked — show the top reason (most impactful first).
  const priority = [
    'subscription_inactive',
    'trial_cap_reached',
    'monthly_cap_reached',
    'sms_disabled',
    'nickname_missing',
  ];
  const top = priority.find((k) => status.reasons.includes(k)) || status.reasons[0];
  const meta = REASON_COPY[top] || {
    title: 'SMS is blocked',
    body: 'Contact support if you were expecting SMS to be active.',
    cta: null,
  };

  return (
    <div
      className="panel"
      data-testid="sms-status-blocked"
      style={{
        padding: 14,
        borderLeft: '3px solid var(--amber, #f59e0b)',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <AlertTriangle size={18} color="var(--amber, #f59e0b)" style={{ flex: '0 0 auto', marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }} data-testid="sms-status-reason-title">
          {meta.title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--mute)', marginTop: 3 }}>{meta.body}</div>
        {status.reasons.length > 1 && (
          <div style={{ fontSize: 11, color: 'var(--mute)', marginTop: 4 }}>
            + {status.reasons.length - 1} other issue{status.reasons.length > 2 ? 's' : ''} to fix after this.
          </div>
        )}
        {meta.cta && (
          <Link
            to={meta.cta.to}
            className="btn s"
            data-testid="sms-status-cta"
            style={{ marginTop: 8, display: 'inline-block', fontSize: 12 }}
          >
            {meta.cta.label} →
          </Link>
        )}
      </div>
    </div>
  );
};

export default SmsStatusCard;
